import React, { useState } from "react"

import api from "@/services/api"
import apiEndPoints from "@/consts/apiEndPoints"
import { Yup } from "@/services/yup"
import { sumProp } from "@/services/utils"
import { useReportsContext } from "@/contexts/Reports"

import Table from "@/components/Table"
import Filter from "@/components/Reports/Filter"

import ComboboxCompanies from "@/components/Combobox/Companies"
import ComboboxCustomers from "@/components/Combobox/Customers"
import formatter from "@/services/formatter"
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
} from "@material-ui/core"
import { InsertDriveFile, ReceiptOutlined } from "@material-ui/icons"
import { downloadPaymentSlip } from "@/http/download-payment-slip"
import { DownloadPaymentSlipButton } from "@/components/DownloadPaymentSlipButton"

// ╔╦╗╔═╗╔╦╗╔═╗╔╦╗╔═╗╔╦╗╔═╗
// ║║║║╣  ║ ╠═╣ ║║╠═╣ ║ ╠═╣
// ╩ ╩╚═╝ ╩ ╩ ╩═╩╝╩ ╩ ╩ ╩ ╩

const initialState = {
  report: [],
}

//  ╔╦╗╔═╗╔╦╗╦ ╦╔═╗╔╦╗╔═╗
//  ║║║║╣  ║ ╠═╣║ ║ ║║╚═╗
//  ╩ ╩╚═╝ ╩ ╩ ╩╚═╝═╩╝╚═╝

const schema = Yup.object().shape({
  di: Yup.string().required("Informe a data inicial").nullable(),
  df: Yup.string().required("Informe a data final").nullable(),
})

export default function TitlesInOpenReport() {
  const { setLoadingText, minDate, maxDate } = useReportsContext()
  const [data, setData] = useState({ ...initialState })

  const fetchReport = async (values) => {
    setData({ ...initialState })
    setLoadingText("Montando relatório de títulos em aberto...")

    try {
      const response = await api.post(apiEndPoints.reports.titlesInOpen, {
        filtros: { ...values },
      })

      const report = response.data.report

      setData({ report })
    } catch (error) {
      setData({ ...initialState })
    }

    setLoadingText("")
  }

  const handleSubmit = (values) => {
    const params = { ...values }
    fetchReport(params)
  }

  // Example of the data returned from the API
  //   {
  // 	"0": {
  // 		"cr_num_duplicata": "157046.0",
  // 		"cr_ordem": "D/E",
  // 		"id_cia": 1,
  // 		"cr_data_emissao": "2022-01-11T00:00:00.000-02:00",
  // 		"cr_data_vencto": "2022-05-03T00:00:00.000-03:00",
  // 		"cr_valor": "374.54",
  // 		"cod_cliente": "32241181000134",
  // 		"atraso": "1582.0",
  // 		"cliente": "BF MATERIAIS DE CONSTRUCAO EIRELI",
  // 		"vencido": "374.54",
  // 		"avencer": "0.0",
  // 		"emp_tipo_pessoa": "J",
  // 		"origem": " ",
  // 		"cr_serie": "0  "
  // 	}
  // }

  return (
    <>
      <Filter
        onSubmit={handleSubmit}
        schema={schema}
        fields={[
          {
            name: "di",
            label: "Data Inicial",
            type: "date",
            defaultValue: minDate,
            size: 2,
          },
          {
            name: "df",
            label: "Data Final",
            type: "date",
            defaultValue: maxDate,
            size: 2,
          },
          {
            name: "cod_cia",
            label: "Companhia",
            defaultValue: 1,
            size: 2,
            component: ComboboxCompanies,
            componentProps: {
              disableClearable: true,
            },
          },
          {
            name: "cod_cliente",
            label: "Cliente",
            defaultValue: "Todos",
            component: ComboboxCustomers,
            componentProps: {
              onLoadOptions: (customer) => ({
                value: customer.id,
                label: customer.emp_razao_social,
              }),
            },
            size: 3,
          },
        ]}
      />

      {data.report.length > 0 && (
        <>
          {/* ==== TÍTULOS EM ABERTO ==== */}
          <Table
            size="small"
            data={data.report}
            options={{ total: true }}
            columns={[
              {
                title: "Cliente",
                field: "cliente",
                render: ({ rowData }) => (
                  <div>
                    {rowData.cliente} -{" "}
                    {formatter(rowData.cod_cliente).toCNPJ()}
                  </div>
                ),
              },
              {
                title: "Duplicata",
                field: "cr_num_duplicata",
              },
              {
                title: "Ordem",
                field: "cr_ordem",
              },
              {
                title: "Emissão",
                field: "cr_data_emissao",
                type: "date",
              },
              {
                title: "Vencimento",
                field: "cr_data_vencto",
                type: "date",
              },
              {
                title: "Valor",
                field: "cr_valor",
                type: "currency",
                align: "right",
                getTotal: ({ data }) => sumProp("cr_valor", data),
              },
              {
                title: "Atraso (dias)",
                field: "atraso",
                type: "integer",
                align: "right",
              },
              {
                title: "Vencido",
                field: "vencido",
                type: "currency",
                align: "right",
                getTotal: ({ data }) => sumProp("vencido", data),
              },
              {
                title: "A Vencer",
                field: "avencer",
                type: "currency",
                align: "right",
                getTotal: ({ data }) => sumProp("avencer", data),
              },
              //
              {
                title: "Boleto",
                field: "",
                align: "center",
                render: ({ rowData }) => (
                  <Box display="flex" justifyContent="center">
                    <DownloadButton rowData={rowData} />
                  </Box>
                ),
              },
            ]}
          />
        </>
      )}
    </>
  )
}

function DownloadButton({ rowData }) {
  async function handleDownload() {
    await downloadPaymentSlip({
      duplicateId: rowData.cr_num_duplicata,
      duplicateOrder: rowData.cr_ordem,
      duplicateSeries: rowData.cr_serie,
      companyId: rowData.id_cia,
      customerId: rowData.cod_cliente,
    })
  }

  return (
    <DownloadPaymentSlipButton
      onClick={handleDownload}
      code={rowData.emite_boleto ? rowData.cod_portador : undefined}
    />
  )
}
